import React, { useState } from 'react'
import { Check } from 'lucide-react'
import { SiGoogleanalytics, SiMeta, SiGoogleads, SiTiktok, SiMailchimp, SiStripe, SiPaypal, SiQuickbooks } from 'react-icons/si'
import BottomMenu from './BottomMenu'

interface ToolsUsedProps {
  initialValue?: string[]
  onNext: (value: string[]) => void
  onBack?: () => void
  onSkip?: () => void
  /** Extra line shown under the grid (e.g. store-less "connect from Integrations"). */
  note?: string
}

// Textless (icon-only) brand marks, brand-coloured, matching the mockup. Klaviyo
// has no simple-icons mark, so we render its "K" tile (as the mockup does too).
const ICON_SIZE = 24
const KlaviyoMark = () => (
  <span className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded tw-bg-green-600 tw-text-xs tw-font-bold tw-text-white">K</span>
)

const TOOLS = [
  { value: 'google_analytics', label: 'Google Analytics', icon: <SiGoogleanalytics size={ICON_SIZE} color="#E37400" /> },
  { value: 'meta_ads', label: 'Meta Ads', icon: <SiMeta size={ICON_SIZE} color="#0866FF" /> },
  { value: 'google_ads', label: 'Google Ads', icon: <SiGoogleads size={ICON_SIZE} color="#4285F4" /> },
  { value: 'tiktok_ads', label: 'TikTok Ads', icon: <SiTiktok size={ICON_SIZE} color="#000000" /> },
  { value: 'klaviyo', label: 'Klaviyo', icon: <KlaviyoMark /> },
  { value: 'mailchimp', label: 'Mailchimp', icon: <SiMailchimp size={ICON_SIZE} color="#FFB700" /> },
  { value: 'stripe', label: 'Stripe', icon: <SiStripe size={ICON_SIZE} color="#635BFF" /> },
  { value: 'paypal', label: 'PayPal', icon: <SiPaypal size={ICON_SIZE} color="#003087" /> },
  { value: 'quickbooks', label: 'QuickBooks', icon: <SiQuickbooks size={ICON_SIZE} color="#2CA01C" /> },
]

/** #693: "Which tools does your business use?" — multi-select, textless brand marks. */
const ToolsUsed: React.FC<ToolsUsedProps> = ({ initialValue = [], onNext, onBack, onSkip, note }) => {
  const [selected, setSelected] = useState<string[]>(initialValue)
  const toggle = (v: string) =>
    setSelected((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))

  return (
    <div className="tw-p-4 md:tw-p-8 tw-pb-32">
      <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 tw-gap-3">
        {TOOLS.map((t) => {
          const isSelected = selected.includes(t.value)
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => toggle(t.value)}
              aria-pressed={isSelected}
              className={`tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-bg-white tw-px-3 tw-py-5 tw-transition hover:tw-border-slate-300 ${
                isSelected ? 'tw-border-indigo-500 tw-ring-2 tw-ring-indigo-200' : 'tw-border-slate-200'
              }`}
            >
              <span className="tw-flex tw-h-7 tw-items-center tw-justify-center">{t.icon}</span>
              <span className="tw-text-center tw-text-sm tw-font-medium tw-text-slate-800">{t.label}</span>
              {isSelected && (
                <span className="tw-absolute tw-top-2 tw-right-2 tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-bg-indigo-600 tw-text-white">
                  <Check className="tw-h-2.5 tw-w-2.5" />
                </span>
              )}
            </button>
          )
        })}
      </div>
      {note && <div className="tw-mt-5 tw-text-sm tw-text-slate-500">{note}</div>}

      <BottomMenu
        onPrev={onBack}
        onNext={() => onNext(selected)}
        onSkip={onSkip}
        skipButtonLabel="Skip"
        text={selected.length ? `${selected.length} selected` : ''}
        textVariant="info"
      />
    </div>
  )
}

export default ToolsUsed
